/**
 * The Android app's extras that act on the running app (mobile M7), mounted
 * once inside the shell. Renders nothing but the share-in sheet; off-native
 * every piece is inert.
 *
 *  * **Shortcuts** — a long-press shortcut's tap opens its screen.
 *  * **Widget** — whenever the dashboard is fetched, the home-screen widget
 *    gets "Due by month end" as finished strings, stamped with when the
 *    figures are from (the offline time, when they came from the saved copy).
 *  * **Share-in** — a PDF shared from another app opens `ShareInSheet`.
 */

import { useQueryClient } from "@tanstack/react-query";
import * as React from "react";
import { useNavigate } from "react-router-dom";

import { ShareInSheet } from "@/components/statements/ShareInSheet";
import type { DashboardSummary } from "@/lib/api/client";
import { useCommands } from "@/lib/commands";
import { getOfflineState } from "@/lib/offline";
import { shortcutTaps } from "@/lib/shortcuts";
import { widgetHidesAmounts, widgetSummary } from "@/lib/widgetSummary";
import { haptic, isAndroidApp } from "@/platform";
import { setMonthEndWidget } from "@/platform/widget";

export function NativeBridges() {
  const navigate = useNavigate();
  const { openPlanner, syncNow } = useCommands();
  const qc = useQueryClient();

  React.useEffect(
    () =>
      shortcutTaps.listen((id) => {
        haptic("tick");
        if (id === "which") openPlanner();
        else if (id === "spends") navigate("/transactions");
        else if (id === "sync") syncNow();
      }),
    [navigate, openPlanner, syncNow],
  );

  React.useEffect(() => {
    if (!isAndroidApp()) return;
    const push = (data: DashboardSummary) => {
      const { offline, asOf } = getOfflineState();
      void setMonthEndWidget(widgetSummary(data, offline && asOf ? asOf : Date.now(), widgetHidesAmounts()));
    };
    const current = qc.getQueryData<DashboardSummary>(["dashboard"]);
    if (current) push(current);
    return qc.getQueryCache().subscribe((event) => {
      if (event.type !== "updated" || event.action.type !== "success") return;
      if (event.query.queryKey[0] !== "dashboard") return;
      push(event.query.state.data as DashboardSummary);
    });
  }, [qc]);

  return isAndroidApp() ? <ShareInSheet /> : null;
}
